import { inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { useToaster } from 'app/shared/composables/use-toaster';
import type { SiteId } from 'app/site-detail/models/Site';

import { AnalyticsService } from '../../shared/services/analytics.service';
import { InterventionsStore } from '../interventions.store';

export type InterventionAnalyticsSource = 'Installation' | 'Alert resolution' | 'Notification';

export interface UseInterventionOptions {
    analyticsSource?: InterventionAnalyticsSource;
}

export function useRequestIntervention(options: UseInterventionOptions = {}) {
    const interventions = inject(InterventionsStore);
    const analyticsService = inject(AnalyticsService);
    const translate = inject(TranslateService);
    const toaster = useToaster();

    const analyticsSource = options.analyticsSource ?? 'Installation';
    const loading = signal(false);

    const requestIntervention = async (siteId: SiteId): Promise<boolean> => {
        if (loading()) return false;

        loading.set(true);
        try {
            analyticsService.remoteServiceRequested(analyticsSource);
            await interventions.request(siteId);
            toaster.success({ title: translate.instant('TOASTER_INTERVENTION_REQUEST_SENT_MESSAGE') });
            return true;
        } catch {
            toaster.error({ title: translate.instant('ERROR_WHILE_OPENING_NEW_INTERVENTION'), duration: 5 * 1000 });
            return false;
        } finally {
            loading.set(false);
        }
    };

    return { requestIntervention, loading: loading.asReadonly() };
}
